import { createFileRoute, redirect, Link } from "@tanstack/react-router";

import { Cpm } from "@/components/CampaignAnalytics";
import { createHead } from "@/components/createHead";
import { Description } from "@/components/Description";
import { FeedEmbed } from "@/components/FeedEmbed";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { MarketplaceBody } from "@/components/MarketplacePage";
import { FeedAvatar } from "@/components/ui/avatar";
import { P } from "@/components/ui/typography";
import { UserAnalyticsSummary } from "@/components/UserAnalyticsSummary";
import { getMonetizedFeedsQueryOptions, SearchResult } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_marketplace-layout/ads/$feedId")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const data = await queryClient.ensureInfiniteQueryData(
      getMonetizedFeedsQueryOptions({
        algo_id: parseInt(feedId),
        days: 30,
      }),
    );

    if (!data.pages[0]?.results.length) {
      throw redirect({ to: "/ads" });
    }

    const feed = data.pages[0]?.results[0];

    if (!feed) {
      throw redirect({ to: "/ads" });
    }

    return {
      feed: feed as Omit<SearchResult, "algorithm">,
    };
  },
  head: ({ loaderData }) => {
    const result = loaderData?.feed as SearchResult;
    return createHead({
      title: `${result.algorithm.display_name} Bluesky Feed | Graze`,
      ogTitle: `${result.algorithm.display_name} Bluesky Feed`,
      description: result.algorithm.description,
      pathname: `/ads/${result.algorithm.id}`,
      ogImage: `/ads/$feedId/og.png`,
      ogParams: { feedId: result.algorithm.id },
    });
  },
});

function RouteComponent() {
  const loaderData = Route.useLoaderData();
  const result = loaderData?.feed as SearchResult;
  const feed = result.algorithm;

  return (
    <MarketplaceBody>
      <div className="pt-4 md:pt-20 md:pb-12 flex flex-col gap-4 md:gap-8 items-center">
        <FeedAvatar feed={feed} className="w-16 h-16 md:w-24 md:h-24" />

        <h1 className="text-5xl md:text-8xl font-bold instrument-serif text-center">
          {feed.display_name}
        </h1>
        <div className="flex flex-col gap-0.5 md:gap-1 flex-1">
          <Link
            className="text-lg md:text-xl text-slate-dim"
            to="/ads/user/$userId"
            params={{ userId: feed.user_id.toString() }}
          >
            @{result.username}
          </Link>
        </div>
      </div>

      <div className="flex gap-4">
        <div
          className={cn(
            "hidden md:flex",
            "text-lg md:text-2xl font-semibold text-slate-dim bg-greena-6 dark:bg-greendarka-6 text-green-12 dark:text-greendark-12 rounded-lg p-4",
            "flex-col gap-2 shrink-0",
          )}
        >
          <div className="uppercase text-xs">Impressions</div>
          <div>
            <Cpm status={feed} />
            <span className="text-sm opacity-80">{" / 1000"}</span>
          </div>
        </div>
        <UserAnalyticsSummary
          variant="compact"
          analytics={feed.stats.details || []}
          summary={feed.stats.summary || {}}
        />
      </div>

      <div className="flex flex-col gap-8">
        <Description className="bg-slate-subtle border-l-4 border-slate-7 dark:border-slatedark-7 pl-4 py-4">
          {feed.description}
        </Description>

        {feed.algorithm_bio && (
          <P className="whitespace-pre-wrap text-xl">
            <MarkdownRenderer>{feed.algorithm_bio}</MarkdownRenderer>
          </P>
        )}
      </div>
      <FeedEmbed
        feedId={`${feed.id}`}
        mode="masonry-infinite"
        frameless={true}
      />
    </MarketplaceBody>
  );
}
